"use client";

import { findProfile, PROFILES } from "@kedaat/definitions";
import {
  addDays,
  formatPlainDate,
  formatTime,
  israelDateOf,
  ZmanimEvaluator,
} from "@kedaat/engine";
import { findLocality, LOCALITIES } from "@kedaat/locations";
import { useEffect, useMemo } from "react";
import { useEngine } from "@/lib/hooks";
import { weekdayName } from "@/lib/month";
import { holyPeriods } from "@/lib/periods";
import { settingsToQuery } from "@/lib/settings";
import { useSettings } from "@/lib/useSettings";
import { computeDay, formatGregorian, pointFor } from "@/lib/zmanim";
import { LocalityPicker } from "./LocalityPicker";
import { ProfilePicker } from "./ProfilePicker";

/** Enough days for about ten Shabbatot and the holidays among them. */
const DAYS_AHEAD = 70;

export function UpcomingShabbatot() {
  const engine = useEngine();
  const { settings, update, ready } = useSettings();

  const locality = findLocality(settings.locality) ?? LOCALITIES[0]!;
  const profile = findProfile(settings.profile) ?? PROFILES[0]!;
  const candleMinutes = settings.candleMinutes[locality.code] ?? locality.candle.minutes;

  useEffect(() => {
    if (!ready) return;
    const query = settingsToQuery(settings, null);
    if (window.location.search !== query) window.history.replaceState(null, "", query);
  }, [ready, settings]);

  const periods = useMemo(() => {
    if (!engine || !ready) return null;
    const evaluator = new ZmanimEvaluator(engine.astro, pointFor(locality));
    // Start from yesterday so a Shabbat that began last night is still listed.
    const start = addDays(israelDateOf(Date.now()), -1);
    const days = Array.from({ length: DAYS_AHEAD }, (_, i) =>
      computeDay(
        evaluator,
        engine.calendar,
        addDays(start, i),
        profile,
        settings.overrides,
        candleMinutes,
      ),
    );
    const byDate = new Map(days.map((d) => [formatPlainDate(d.info.date), d]));
    return holyPeriods(days.map((d) => d.info)).map((period) => ({
      period,
      days: period.days.map((info) => byDate.get(formatPlainDate(info.date))!),
    }));
  }, [engine, ready, locality, profile, settings.overrides, candleMinutes]);

  return (
    <div className="space-y-5">
      <h1 className="font-serif text-3xl font-bold">שבתות וחגים</h1>
      <section className="grid gap-3 sm:grid-cols-2">
        <LocalityPicker
          locality={locality}
          onSelect={(l) => update(() => ({ locality: l.code }))}
        />
        <ProfilePicker
          profile={profile}
          overrideCount={Object.keys(settings.overrides).length}
          onSelect={(id) => update(() => ({ profile: id, overrides: {} }))}
          onResetOverrides={() => update(() => ({ overrides: {} }))}
        />
      </section>

      {periods ? (
        <ol className="grid gap-3 sm:grid-cols-2">
          {periods.map(({ period, days }) => {
            const end = days.at(-1)!.special.shabbatEnd;
            return (
              <li
                key={formatPlainDate(period.start.date)}
                className="rounded-lg border border-line bg-card px-4 py-3"
              >
                <h2 className="font-serif text-xl font-bold">{period.title}</h2>
                <p className="text-xs text-muted">
                  {formatGregorian(period.start.date)} · {period.start.hebrewDate}
                </p>
                <dl className="mt-3 space-y-2">
                  {days.map(({ info, special }) =>
                    special.candleLighting ? (
                      <div
                        key={formatPlainDate(info.date)}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <dt className="text-sm">
                          הדלקת נרות · {weekdayName(info.date)}
                          {special.candleLighting.afterNightfall && (
                            <span className="block text-xs text-muted">
                              לאחר צאת הכוכבים, מאש קיימת
                            </span>
                          )}
                        </dt>
                        <dd className="text-xl font-bold tabular-nums text-accent">
                          {formatTime(special.candleLighting.ms, special.candleLighting.rounding)}
                        </dd>
                      </div>
                    ) : null,
                  )}
                  {end && (
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-sm">
                        צאת · {weekdayName(period.end.date)}
                        <span className="block text-xs text-muted">
                          8.5° · לר״ת {formatTime(end.rabbeinuTam.ms, end.rabbeinuTam.rounding)}
                        </span>
                      </dt>
                      <dd className="text-xl font-bold tabular-nums text-accent">
                        {formatTime(end.time.ms, end.time.rounding)}
                      </dd>
                    </div>
                  )}
                </dl>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" aria-busy="true" aria-label="מחשב זמנים">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-lg bg-card" />
          ))}
        </div>
      )}

      <p className="text-sm text-muted">
        הדלקת נרות {candleMinutes} דק׳ לפני השקיעה לפי מנהג {locality.nameHe}. ניתן לשנות בעמוד זמני
        היום.
      </p>
    </div>
  );
}
